import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import InvestorLayout from '../../components/InvestorLayout';
import { Search } from 'lucide-react';

export default function PayoutHistory({ user, onLogout }) {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/investor/payouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayouts(response.data);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPayouts = payouts.filter(p =>
    p.payout_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.investment_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPayouts = payouts.reduce((sum, p) => sum + p.amount, 0);

  return (
    <InvestorLayout user={user} onLogout={onLogout} currentPage="payouts">
      <div className="space-y-6" data-testid="payout-history-page">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Payout History
          </h1>
          <p className="text-muted-foreground text-lg">Track all your received payouts</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">Total Payouts</p>
            <p className="text-3xl font-bold font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              ₹{totalPayouts.toLocaleString('en-IN')}
            </p>
          </div>
          <div className="card p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">Total Transactions</p>
            <p className="text-3xl font-bold font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {payouts.length}
            </p>
          </div>
          <div className="card p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">Completed</p>
            <p className="text-3xl font-bold font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {payouts.filter(p => p.status === 'completed').length}
            </p>
          </div>
        </div>

        <div className="card">
          <div className="p-6 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                data-testid="search-payouts-input"
                type="text"
                placeholder="Search payouts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="payouts-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Payout ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Investment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : filteredPayouts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                      No payouts found
                    </td>
                  </tr>
                ) : (
                  filteredPayouts.map((payout) => (
                    <tr key={payout.payout_id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-sm">{payout.payout_id}</td>
                      <td className="px-6 py-4 font-mono text-sm">{payout.investment_id}</td>
                      <td className="px-6 py-4 font-mono font-semibold text-success">
                        ₹{payout.amount.toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4 capitalize">{payout.payout_type}</td>
                      <td className="px-6 py-4 text-sm">
                        {new Date(payout.payout_date).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          payout.status === 'completed' ? 'bg-success/10 text-success' :
                          payout.status === 'pending' ? 'bg-warning/10 text-warning' :
                          'bg-destructive/10 text-destructive'
                        }`}>
                          {payout.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </InvestorLayout>
  );
}